// Snowplow Analytics Browser Tracker Setup
// see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/quick-start-guide/
// and https://github.com/Upheal/upheal-analytics/blob/main/book/tracking/web-app.md
import { newTracker, setUserId } from '@snowplow/browser-tracker';
import {
    LinkClickTrackingPlugin,
    enableLinkClickTracking,
} from '@snowplow/browser-plugin-link-click-tracking';
import {
    ErrorTrackingPlugin,
    trackError,
} from '@snowplow/browser-plugin-error-tracking';
import { TimezonePlugin } from '@snowplow/browser-plugin-timezone';
import { User } from '../../types';
import { LOGGER_ENABLED } from '@upheal/utils/logger';

const MAX_MESSAGE_LENGTH = 2048;
const MAX_STACK_TRACE_LENGTH = 8192;

const truncate = (value: string | undefined, maxLength: number) =>
    value ? value.substring(0, maxLength) : value;

const buildTruncatedError = (
    message: string,
    stack?: string,
): Error => {
    const error = new Error(truncate(message, MAX_MESSAGE_LENGTH));
    error.stack = truncate(stack, MAX_STACK_TRACE_LENGTH);
    return error;
};

const enableTruncatedErrorTracking = () => {
    window.addEventListener('error', (event: ErrorEvent) => {
        trackError({
            message: truncate(event.message, MAX_MESSAGE_LENGTH)!,
            error: buildTruncatedError(event.message, event.error?.stack),
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const message =
            event.reason instanceof Error
                ? event.reason.message
                : String(event.reason);
        const stack =
            event.reason instanceof Error ? event.reason.stack : undefined;
        const truncatedMessage = truncate(
            `Unhandled Promise Rejection: ${message}`,
            MAX_MESSAGE_LENGTH,
        )!;

        trackError({
            message: truncatedMessage,
            error: buildTruncatedError(truncatedMessage, stack),
        });
    });
};

const DEFAULT_COLLECTOR_URL =
    import.meta.env.VITE_REACT_APP_SERVER_STAGE === 'prod'
        ? 'https://sp-collector-prod.api.upheal.io'
        : 'https://sp-collector.api.upheal.io';

export const COLLECTOR_URL: string =
    import.meta.env.VITE_REACT_APP_SNOWPLOW_COLLECTOR_URL ?? DEFAULT_COLLECTOR_URL;

const isTrainingDemoRoute = () =>
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/training-demo');

export const initSnowplowTracker = () => {
    if (!LOGGER_ENABLED || isTrainingDemoRoute()) {
        return;
    }

    // Initialization options - https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/tracker-setup/initialization-options/
    newTracker(
        import.meta.env.VITE_REACT_APP_SERVER_STAGE === 'prod' ? 'prod' : 'dev',
        COLLECTOR_URL,
        {
            appId: 'app.upheal.io',
            discoverRootDomain: true,
            respectDoNotTrack: true,
            cookieSameSite: 'Lax',
            stateStorageStrategy: 'cookieAndLocalStorage',
            plugins: [
                LinkClickTrackingPlugin(),
                ErrorTrackingPlugin(),
                TimezonePlugin(),
            ],
            contexts: {
                webPage: true,
                session: true,
            },
            // custom path to mitigate impact of adblockers - see https://docs.snowplow.io/docs/get-started/tracking/cookies-and-ad-blockers/#2-mitigating-the-impact-of-ad-blockers).
            postPath: '/track',
        },
    );

    // For details about `refreshLinkTracking` and implicit `trackLinkClick`
    // see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/tracking-events/#link-click-tracking
    enableLinkClickTracking();

    // For details how to track checked errors (use `trackError`) and unchecked errors captured here
    // via `enableTruncatedErrorTracking()`, see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/plugins/error-tracking/
    enableTruncatedErrorTracking();
};

export const setSnowplowUser = (user: User) => {
    if (!LOGGER_ENABLED || isTrainingDemoRoute()) {
        return;
    }

    // Set user id by using one of following methods:
    // see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/tracker-setup/additional-options/
    setUserId(user.id);
};
